import styled from "@emotion/styled";
import { captureException } from "@sentry/react";
import { useConnection } from "@solana/wallet-adapter-react";
import type { PublicKey } from "@solana/web3.js";
import { MSOL_MINT, NATIVE_SOL } from "@stepfinance/common";
import { lightGrey2 } from "@stepfinance/react";
import { Select } from "antd";
import type { FC, ReactNode } from "react";
import { useEffect, useState } from "react";

import { JITO_SOL_MINT, jitoStake } from "../../actions/jito/stake";
import { jitoUnstake } from "../../actions/jito/unstake";
import { lidoStake } from "../../actions/lido/stake";
import { lidoUnstake } from "../../actions/lido/unstake";
import { marinadeStake } from "../../actions/marinade/stake";
import { marinadeUnstake } from "../../actions/marinade/unstake";
import { soceanStake } from "../../actions/socean/stake";
import { soceanUnstakeInstant } from "../../actions/socean/unstake";
import { useAccountsContextRefresher } from "../../contexts/accounts";
import { useAppConfig } from "../../contexts/app/config";
import { useLanguageContext } from "../../contexts/language/language";
import type { MultiTxnNotificationStrings } from "../../contexts/language/languageTypes";
import { useLiquidity } from "../../contexts/liquidity";
import { useStakingContext } from "../../contexts/staking";
import { useSwapContextState } from "../../contexts/swap";
import useLidoStaking from "../../hooks/staking/useLidoStaking";
import { useScnSolAPY } from "../../hooks/staking/useSoceanStaking";
import { useConnectedWallet } from "../../hooks/wallet/useConnectedWallet";
import { STSOL_MINT } from "../../models/lido";
import { SCN_MINT } from "../../models/socean";
import { PoolOperation } from "../../utils/pools";
import { handlePreSendTxnError } from "../../utils/transactions";
import { formatPct, formatStringWithPlaceholders } from "../../utils/utils";
import { IconTextContainer, TokenIconText } from "../shared";
import { TokenIcon } from "../TokenIcon";
import { STAKE_ENUM, Staking } from "./Staking";
import type { StakeAction } from "./StakingTabs";

const { Option } = Select;

export type StakeOption = "Marinade" | "Socean" | "Lido" | "Jito";

type SOLStakingOption = {
  name: StakeOption;
  apy: number;
  stakedMint: PublicKey;
  stakedSymbol: string;
  stake: (amount: number, stakeTokenAmount: number) => Promise<any>;
  unstake: (amount: number, stakeTokenAmount: number) => Promise<any>;
};

type StakingModalProps = {
  stakeOption?: StakeOption;
  stakeAction: StakeAction;
  closeModal: () => void;
};

export const StakingModal: FC<StakingModalProps> = ({ stakeOption, stakeAction, closeModal }) => {
  const wallet = useConnectedWallet();
  const { connection } = useConnection();
  const refreshAccounts = useAccountsContextRefresher();
  const { A, B, setPoolOperation, marinadeUnstakeFee } = useSwapContextState();
  const { marinadeState } = useLiquidity();
  const marinadeAPY = marinadeState ? marinadeState.apy : 0;
  const scnSolAPY = useScnSolAPY();
  const { lidoAPR } = useLidoStaking();
  const [pendingTx, setPendingTx] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  const isStaking = stakeAction === STAKE_ENUM.STAKE;
  const [stakingOptions, setStakingOptions] = useState<SOLStakingOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<SOLStakingOption>();
  const { solStakingAPYMap } = useStakingContext();
  const { transactionVersion } = useAppConfig();

  const {
    currentLanguageData: {
      common,
      pages: {
        swapLP: { notifications: swapNotifications },
        dashboard: {
          modules: {
            balances: { stakingModal: stakingModalText },
          },
        },
      },
    },
  } = useLanguageContext();

  useEffect(() => {
    void (() => {
      if (!wallet) {
        return;
      }

      const multiTxnStakeNotifications: MultiTxnNotificationStrings = {
        ...swapNotifications,
        ...common.notifications,
      };

      const options: SOLStakingOption[] = [];
      options.push({
        name: "Marinade",
        apy: marinadeAPY,
        stakedMint: MSOL_MINT,
        stakedSymbol: "mSOL",
        stake: (amount: number, stakeTokenAmount: number) =>
          marinadeStake(wallet, connection, amount, setPendingConfirmation, stakeTokenAmount, common.notifications),
        unstake: (amount: number, stakeTokenAmount: number) =>
          marinadeUnstake(wallet, connection, amount, setPendingConfirmation, stakeTokenAmount, common.notifications),
      });

      options.push({
        name: "Socean",
        apy: scnSolAPY ? scnSolAPY / 100 : 0,
        stakedMint: SCN_MINT,
        stakedSymbol: "scnSOL",
        stake: (amount: number, stakeTokenAmount: number) =>
          soceanStake(wallet, connection, amount, stakeTokenAmount, setPendingConfirmation, multiTxnStakeNotifications),
        unstake: (amount: number, stakeTokenAmount: number) =>
          soceanUnstakeInstant(
            wallet,
            connection,
            amount,
            stakeTokenAmount,
            setPendingConfirmation,
            multiTxnStakeNotifications,
            transactionVersion,
          ),
      });

      options.push({
        name: "Lido",
        apy: lidoAPR / 100,
        stakedMint: STSOL_MINT,
        stakedSymbol: "stSOL",
        stake: (amount: number, stakeTokenAmount: number) =>
          lidoStake(wallet, connection, amount, stakeTokenAmount, setPendingConfirmation, common.notifications),
        unstake: (amount: number, stakeTokenAmount: number) =>
          lidoUnstake(wallet, connection, amount, stakeTokenAmount, setPendingConfirmation, common.notifications),
      });

      options.push({
        name: "Jito",
        apy: solStakingAPYMap ? solStakingAPYMap["jito"] : 0,
        stakedMint: JITO_SOL_MINT,
        stakedSymbol: "JitoSOL",
        stake: (amount: number, stakeTokenAmount: number) =>
          jitoStake(wallet, connection, amount, stakeTokenAmount, setPendingConfirmation, common.notifications),
        unstake: (amount: number, stakeTokenAmount: number) =>
          jitoUnstake(wallet, connection, amount, stakeTokenAmount, setPendingConfirmation, common.notifications),
      });

      setStakingOptions(options.sort((a, b) => b.apy - a.apy));
      if (stakeOption) {
        setSelectedOption(options.find((option) => option.name === stakeOption));
      } else {
        setSelectedOption(options[0]);
      }
    })();
  }, [
    wallet,
    connection,
    marinadeAPY,
    solStakingAPYMap,
    stakeOption,
    scnSolAPY,
    lidoAPR,
    common.notifications,
    swapNotifications,
    transactionVersion,
  ]);

  useEffect(() => {
    if (!selectedOption) {
      return;
    }
    if (isStaking) {
      A.setMint(NATIVE_SOL);
      B.setMint(selectedOption.stakedMint);
    } else {
      B.setMint(NATIVE_SOL);
      A.setMint(selectedOption.stakedMint);
    }

    setPoolOperation(isStaking ? PoolOperation.MarinadeStake : PoolOperation.MarinadeUnstake);
  }, [A, B, isStaking, setPoolOperation, stakingOptions, selectedOption]);

  let buttonMessage: string | ReactNode[] = formatStringWithPlaceholders(
    common.stepButtonLabels.stakeOrUnstakeAmountOfToken,
    isStaking ? common.stake : common.unstake,
    parseFloat(A.amount),
    isStaking ? "SOL" : selectedOption ? selectedOption.stakedSymbol : "",
  );
  if (!parseFloat(A.amount)) {
    buttonMessage = common.stepButtonLabels.enterAmount;
  } else if (pendingConfirmation) {
    buttonMessage = common.stepButtonLabels.awaitingTransaction;
  } else if (pendingTx) {
    buttonMessage = common.stepButtonLabels.approveTransaction;
  } else if (!A.sufficientBalance()) {
    buttonMessage = formatStringWithPlaceholders(
      common.stepButtonLabels.insufficientTokenAmount,
      isStaking ? "SOL" : selectedOption ? selectedOption.stakedSymbol : "",
    );
  }

  function handleOptionChange(value: any, _: any) {
    setSelectedOption(stakingOptions.find((option) => option.name === value));
  }

  const disabled =
    !wallet ||
    (parseFloat(A.amount) && !A.sufficientBalance()) ||
    !parseFloat(A.amount) ||
    pendingTx ||
    pendingConfirmation;
  return (
    <>
      <StyledSelect
        style={{ width: "100%" }}
        onChange={handleOptionChange}
        size="large"
        value={selectedOption ? selectedOption.name : ""}
        loading={stakingOptions.length === 0}
      >
        {stakingOptions.map((option) => {
          return (
            <Option key={option.name} value={option.name}>
              <SelectOption>
                <IconTextContainer>
                  <TokenIcon size={20} mintAddress={option.stakedMint.toBase58()} />
                  <TokenIconText bold>{option.name}</TokenIconText>
                </IconTextContainer>
                <span style={{ fontWeight: "bold" }}>{formatPct.format(option.apy)} APY</span>
              </SelectOption>
            </Option>
          );
        })}
      </StyledSelect>

      <Staking
        poolStakingOperation={isStaking ? PoolOperation.MarinadeStake : PoolOperation.MarinadeUnstake}
        stakeAction={stakeAction}
        buttonMessage={buttonMessage}
        stakeActionDisabled={disabled}
        iconSize={25}
        onStakeAction={async () => {
          if (wallet && selectedOption) {
            try {
              setPendingTx(true);
              if (isStaking) {
                await selectedOption.stake(parseFloat(A.amount), parseFloat(B.amount));
              } else {
                await selectedOption.unstake(parseFloat(A.amount), parseFloat(B.amount));
              }
            } catch (err) {
              captureException(err);
              handlePreSendTxnError(err as Error);
            } finally {
              setPendingTx(false);
              setPendingConfirmation(false);
              refreshAccounts();
              closeModal();
            }
          }
        }}
      />
      {!isStaking && selectedOption?.name === "Marinade" && (
        <FeeDisplay>
          <span>
            {formatStringWithPlaceholders(stakingModalText.marinadeFees, formatPct.format(marinadeUnstakeFee))}
          </span>
          {parseFloat(A.amount) > 0 && (
            <span>
              = {(marinadeUnstakeFee * (parseFloat(A.amount) ?? 0) * (marinadeState?.mSOLPricePerSOL ?? 1)).toFixed(6)}{" "}
              SOL
            </span>
          )}
        </FeeDisplay>
      )}
      {!isStaking && selectedOption?.name === "Socean" && (
        <FeeDisplay>
          <span>{formatStringWithPlaceholders(stakingModalText.soceanFees, formatPct.format(0.0003))}</span>
          {parseFloat(A.amount) > 0 && <span>= {(0.0003 * (parseFloat(A.amount) ?? 0)).toFixed(6)} SOL</span>}
        </FeeDisplay>
      )}
    </>
  );
};

const StyledSelect = styled(Select)`
  margin-bottom: 8px;
`;

const SelectOption = styled.div`
  display: flex;
  justify-content: space-between;
`;

const FeeDisplay = styled.div`
  display: flex;
  justify-content: center;
  font-size: 10px;
  color: ${lightGrey2};
  padding-top: 5px;
`;
